import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useQuery, QueryStatus } from "@tanstack/react-query";
import { upperFirst } from "lodash";
import { ChevronRight, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LockedCard } from "@/components/layout/locked-content";
import { GoogleIcon } from "@/components/icons/google";
import { LinkedinIcon } from "@/components/icons/linkedin";
import { MetaIcon } from "@/components/icons/meta";
import { TwitterIcon } from "@/components/icons/twitter";
import { FacebookIcon } from "@/components/icons/facebook";

import { useSession } from "@/hooks/use-session";
import { useMobileMediaQuery } from "@/hooks/use-media-query";
import { getScoreBarColor, chartColors, getBrandCheckStatusClass } from "@/lib/chart";
import { cn, formatSnakeCase, message, sanitizeUrl } from "@/lib/utils";
import { BrandGovernanceApiFactory, type LiveMonitorParams } from "@/apis/brand-os/brand-governance";
import type { BrandCheckItem, BrandCheckStatus, LiveMonitorCreative, LiveMonitorData } from "@/schema/brand-os/brand-governance";

const filterKeys = ["all", "approved", "flagged", "review"] as const;
type FilterKey = (typeof filterKeys)[number];
const videoFormats = new Set(["video", "mp4", "webm", "mov"]);

const platformIcon = (platform?: string): ReactNode => {
  switch (platform) {
    case "google_ads":
      return <GoogleIcon className="size-6" />;
    case "facebook_ads":
      return <FacebookIcon className="size-6" />;
    case "meta_ads":
      return <MetaIcon className="size-6" />;
    case "twitter_ads":
      return <TwitterIcon className="size-6" />;
    case "linkedin_ads":
      return <LinkedinIcon className="size-6" />;
    default:
      return null;
  }
};

function label(label: string): BrandCheckStatus {
  const l = (label ?? "").toLowerCase();
  const status = l.includes("flagged") || l.includes("improvement") ? "flagged" : l.includes("review") ? "review" : "approved";
  return status;
}

function statusCount(items: BrandCheckItem[]): Record<FilterKey, number> {
  const counts: Record<FilterKey, number> = { all: items.length, approved: 0, flagged: 0, review: 0 };
  for (const item of items) counts[item.status]++;
  return counts;
}

function brandCheckItems(creatives: LiveMonitorCreative[]): BrandCheckItem[] {
  const subBrands = [...new Set(creatives.map((c) => c.sub_brand ?? ""))];
  const colorMap = Object.fromEntries(subBrands.map((name, i) => [name, chartColors[i % chartColors.length]]));

  return creatives.map((creative, idx) => ({
    id: `${creative.format}-${idx}`,
    timeAgo: "—",
    brandName: creative.sub_brand ?? "",
    brandColor: colorMap[creative.sub_brand ?? ""],
    platform: creative.platform?.replaceAll("_", " ") ?? "",
    contentType: creative.format,
    score: creative.score ?? 0,
    status: label(creative.label),
    description: creative.ai_verdict ?? "",
    dimensionScores: creative.dimension_scores,
    url: creative.url,
  }));
}

function CreativePreview({ url, format, alt }: { url: string; format: string; alt: string }) {
  const safeUrl = sanitizeUrl(url);
  const isVideo = videoFormats.has(format.toLowerCase());

  if (!safeUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-xs text-muted-foreground">No preview</p>
      </div>
    );
  }

  if (isVideo) {
    return <video src={safeUrl} className="h-full w-full object-contain bg-secondary" muted playsInline controls />;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="h-full w-full">
          <img src={safeUrl} alt={alt} className="h-full w-full object-contain bg-secondary" />
        </TooltipTrigger>
        <TooltipContent side="right">
          <img src={safeUrl} alt={alt} className="size-96 rounded bg-secondary object-contain" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CompanyDetails({ item }: { item: BrandCheckItem }) {
  return (
    <div className="flex gap-6 rounded-xl bg-card">
      <div className="flex w-48 shrink-0 flex-col items-center overflow-hidden rounded-lg bg-muted">
        <CreativePreview url={item.url} format={item.contentType} alt={item.brandName} />
      </div>
      <div className="flex w-full flex-col gap-4 py-3">
        <div className="flex flex-col gap-1">
          <p className="flex flex-row items-center gap-1 text-sm font-medium text-foreground">
            <Sparkles className="size-4 shrink-0 text-violet-500" />
            AI Verdict
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
        </div>
        <div>
          <p className="mb-3 text-xs font-medium capitalize text-foreground">category breakdown</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {Object.entries(item.dimensionScores).map(([key, value]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-28 truncate text-xs text-muted-foreground">{formatSnakeCase(key)}</span>
                <Progress value={value} className="h-2 flex-1 bg-muted" indicatorClassName={getScoreBarColor(value)} />
                <span className="w-6 text-xs text-muted-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandCheckCard({ item }: { item: BrandCheckItem }) {
  const session = useSession();
  const brandName = session.profile.company.company_name;
  const isMobile = useMobileMediaQuery();

  const [expanded, setExpanded] = useState(false);
  const statusClass = getBrandCheckStatusClass(item.status);

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <button type="button" onClick={() => setExpanded((prev) => !prev)} className="w-full items-start gap-x-4 gap-y-1 bg-transparent px-5 py-4 text-left hover:bg-accent">
        <div className="grid grid-cols-[1fr_1fr_1fr_1fr_0.2fr] items-center">
          <p style={{ color: item.brandColor }}>{item.brandName || brandName}</p>
          <div className="flex items-center gap-2">
            {platformIcon(item.platform)}
            <div className="flex flex-col">
              <p className="text-sm text-foreground">{item.platform}</p>
              <p className="text-xs text-muted-foreground/70">{formatSnakeCase(item.contentType)}</p>
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <p className={cn("text-lg font-semibold", statusClass)}>{item.score ?? 0}</p>
            <p className={cn("text-xs capitalize min-w-12", statusClass)}>{item.status}</p>
          </div>
          {!isMobile ? <p className="line-clamp-2 max-w-96 text-sm text-foreground/50">{item.description}</p> : null}
          <ChevronRight className={cn("size-5 shrink-0 text-muted-foreground", expanded && "rotate-90")} />
        </div>
      </button>
      {expanded && (
        <div className="border-t p-4">
          <CompanyDetails item={item} />
        </div>
      )}
    </div>
  );
}

function BrandCheckContent({ data }: { data: LiveMonitorData }) {
  const [filter, setFilter] = useState<FilterKey>("all");

  const items = useMemo(() => brandCheckItems(data.creatives ?? []), [data.creatives]);
  const counts = useMemo(() => statusCount(items), [items]);
  const filtered = useMemo(() => (filter === "all" ? items : items.filter((item) => item.status === filter)), [items, filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {filterKeys.map((key) => (
          <Button
            key={key}
            variant={filter === key ? "default" : "outline"}
            size="sm"
            className={cn("rounded-full px-4 py-2 text-sm font-normal", filter !== key && "text-muted-foreground")}
            onClick={() => setFilter(key)}
          >
            {upperFirst(key)}
            {filter === key && `(${counts[key]})`}
          </Button>
        ))}
      </div>
      {filtered.length ? filtered.map((item) => <BrandCheckCard key={item.id} item={item} />) : <LockedCard message={message} />}
    </div>
  );
}

function BrandCheckComponent({ status, data }: { status: QueryStatus; data?: LiveMonitorData | null }) {
  switch (status) {
    case "pending":
      return <BrandCheckSkeleton />;
    case "error":
      return <LockedCard message={message} />;
    case "success":
      return data ? <BrandCheckContent data={data} /> : <LockedCard message={message} />;
  }
}

export function BrandCheck({ params }: { params: LiveMonitorParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.LiveMonitor(params));
  return <BrandCheckComponent status={status} data={data?.data} />;
}

function BrandCheckSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="grid items-center gap-x-4 gap-y-1 rounded-xl border bg-card px-4 py-3 grid-cols-[1fr_1fr_1fr_1fr_4fr_0.2fr] max-sm:grid-cols-1 max-sm:gap-y-2">
          <div className="flex min-w-0 flex-col gap-1">
            <Skeleton className="h-4 w-16 rounded-md" />
          </div>
          <div className="min-w-0">
            <Skeleton className="h-4 w-20 rounded-md" />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Skeleton className="h-4 w-14 rounded-md" />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Skeleton className="h-7 w-10 rounded-md" />
            <Skeleton className="h-3 w-16 rounded-md" />
          </div>
          <div className="min-w-0 space-y-1">
            <Skeleton className="h-4 w-full max-w-sm rounded-md" />
            <Skeleton className="h-4 w-3/4 max-w-xs rounded-md" />
          </div>
          <Skeleton className="size-5 shrink-0 rounded max-sm:order-last" />
        </div>
      ))}
    </Fragment>
  );
}
